import { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  RefreshCw,
  Filter,
  ChevronLeft,
  ChevronRight,
  Clock,
  Search,
  CheckCircle,
  XCircle,
  AlertCircle,
  X,
  Eye,
} from 'lucide-react';
import {
  searchLogs,
  searchBackends,
  SearchLogEntry,
  SearchBackend,
  SearchLogFilters,
} from '../api/client';
import { formatDateTime, cn } from '../lib/utils';

export default function SearchLogs() {
  const [logs, setLogs]     = useState<SearchLogEntry[]>([]);
  const [backends, setBackends] = useState<SearchBackend[]>([]);
  const [total, setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState<string | null>(null);
  const [selectedLog, setSelectedLog] = useState<SearchLogEntry | null>(null);

  const [filters, setFilters] = useState<SearchLogFilters>({ limit: 50, offset: 0 });
  const [showFilters, setShowFilters] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [logsRes, backendsRes] = await Promise.all([
        searchLogs.list(filters),
        searchBackends.list(),
      ]);
      setLogs(logsRes.logs);
      setTotal(logsRes.total);
      setBackends(backendsRes.backends);
      setError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch search logs';
      console.error('Failed to fetch search logs:', err);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getBackendName = (backendId: string | null) => {
    if (!backendId) return '-';
    const backend = backends.find(b => b.id === backendId);
    return backend?.name || backendId.slice(0, 8) + '...';
  };

  const handleFilterChange = (key: keyof SearchLogFilters, value: string | number | undefined) => {
    setFilters(prev => ({
      ...prev,
      [key]: value === '' ? undefined : value,
      offset: 0,
    }));
  };

  const handlePageChange = (newOffset: number) => {
    setFilters(prev => ({ ...prev, offset: newOffset }));
  };

  const clearFilters = () => {
    setFilters({ limit: 50, offset: 0 });
  };

  const statusForCode = (code: number | null) => {
    if (code === null) return { label: '—',   icon: AlertCircle, color: 'text-gray-400' };
    if (code >= 200 && code < 300) return { label: `${code}`, icon: CheckCircle, color: 'text-emerald-400' };
    if (code === 402) return { label: '402 No Credits',     icon: AlertCircle, color: 'text-amber-400' };
    if (code === 503) return { label: '503 Unavailable',    icon: AlertCircle, color: 'text-amber-400' };
    if (code >= 400)  return { label: `${code}`, icon: XCircle, color: 'text-rose-400' };
    return { label: `${code}`, icon: AlertCircle, color: 'text-muted-foreground' };
  };

  const limit  = filters.limit  ?? 50;
  const offset = filters.offset ?? 0;
  const page   = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  if (loading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading search logs...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold text-gradient">Search Logs</h1>
          <p className="text-muted-foreground mt-1">
            Every <code className="font-mono">POST /v1/search</code> request, regardless of outcome
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              'px-4 py-2.5 rounded-xl flex items-center gap-2 text-sm transition-all',
              showFilters ? 'bg-cyan-500/15 text-cyan-400' : 'btn-secondary'
            )}
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
          <button
            onClick={fetchData}
            className="btn-secondary px-4 py-2.5 rounded-xl flex items-center gap-2 text-sm"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0" />
            <p className="text-sm text-rose-300/80 flex-1">{error}</p>
            <button onClick={() => setError(null)} className="text-rose-400 hover:text-rose-300">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Filters panel */}
      {showFilters && (
        <div className="card-glow rounded-2xl p-5 space-y-4 animate-scale-in">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-muted-foreground mb-2">Backend</label>
              <select
                value={filters.backendId || ''}
                onChange={(e) => handleFilterChange('backendId', e.target.value)}
                className="input-neural w-full px-3 py-2.5 rounded-lg text-sm"
              >
                <option value="">All backends</option>
                {backends.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-2">Device ID</label>
              <input
                type="text"
                value={filters.deviceId || ''}
                onChange={(e) => handleFilterChange('deviceId', e.target.value)}
                className="input-neural w-full px-3 py-2.5 rounded-lg text-sm font-mono"
                placeholder="device-uuid"
              />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-2">Status Code</label>
              <select
                value={filters.statusCode ?? ''}
                onChange={(e) => handleFilterChange('statusCode', e.target.value === '' ? undefined : parseInt(e.target.value, 10))}
                className="input-neural w-full px-3 py-2.5 rounded-lg text-sm"
              >
                <option value="">Any status</option>
                <option value={200}>200 Success</option>
                <option value={400}>400 Invalid Request</option>
                <option value={402}>402 Insufficient Credits</option>
                <option value={403}>403 Blocked (Content Filter)</option>
                <option value={502}>502 Provider Error</option>
                <option value={503}>503 Unavailable</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end">
            <button onClick={clearFilters} className="text-xs text-cyan-400 hover:text-cyan-300">
              Clear filters
            </button>
          </div>
        </div>
      )}

      {/* Logs table */}
      <div className="card-glow rounded-2xl overflow-hidden">
        {logs.length === 0 ? (
          <div className="p-16 flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center">
              <FileText className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground">No search requests yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40">
                <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider">
                  <th className="px-4 py-3">Time</th>
                  <th className="px-4 py-3">Query</th>
                  <th className="px-4 py-3">Backend</th>
                  <th className="px-4 py-3 text-right">Results</th>
                  <th className="px-4 py-3 text-right">Credits</th>
                  <th className="px-4 py-3 text-right">Duration</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const status = statusForCode(log.status_code);
                  const StatusIcon = status.icon;
                  return (
                    <tr
                      key={log.id}
                      className="border-t border-border/50 hover:bg-secondary/20 transition-colors cursor-pointer"
                      onClick={() => setSelectedLog(log)}
                    >
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3 h-3" />
                          {formatDateTime(log.created_at)}
                        </div>
                      </td>
                      <td className="px-4 py-3 max-w-[320px]">
                        <div className="flex items-center gap-2 truncate">
                          <Search className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
                          <span className="truncate" title={log.query}>{log.query}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                        {getBackendName(log.backend_id)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {log.result_count ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {log.cost_credits != null ? log.cost_credits : '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-muted-foreground">
                        {log.response_time_ms != null ? `${log.response_time_ms}ms` : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className={cn('flex items-center gap-1.5 text-xs', status.color)}>
                          <StatusIcon className="w-3.5 h-3.5" />
                          {status.label}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Eye className="w-4 h-4 text-muted-foreground" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Showing {offset + 1}–{Math.min(offset + logs.length, total)} of {total}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(Math.max(0, offset - limit))}
              disabled={offset === 0}
              className="btn-secondary px-3 py-2 rounded-lg disabled:opacity-40"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-3 text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(offset + limit)}
              disabled={offset + limit >= total}
              className="btn-secondary px-3 py-2 rounded-lg disabled:opacity-40"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {selectedLog && (
        <LogDetailModal
          log={selectedLog}
          backendName={getBackendName(selectedLog.backend_id)}
          onClose={() => setSelectedLog(null)}
        />
      )}
    </div>
  );
}

function LogDetailModal({
  log,
  backendName,
  onClose,
}: {
  log: SearchLogEntry;
  backendName: string;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 modal-overlay flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="modal-content rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-lg font-semibold">Search Request Detail</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        <dl className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <dt className="text-xs text-muted-foreground mb-1">Timestamp</dt>
            <dd className="font-mono">{formatDateTime(log.created_at)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground mb-1">Status</dt>
            <dd className="font-mono">{log.status_code ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground mb-1">Backend</dt>
            <dd>{backendName}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground mb-1">Request Type</dt>
            <dd>{log.request_type ?? 'premium'}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground mb-1">Device ID</dt>
            <dd className="font-mono text-xs truncate" title={log.device_id ?? ''}>
              {log.device_id ?? '—'}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground mb-1">Duration</dt>
            <dd className="font-mono">
              {log.response_time_ms != null ? `${log.response_time_ms}ms` : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground mb-1">Result Count</dt>
            <dd className="font-mono">{log.result_count ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground mb-1">Credits Charged</dt>
            <dd className="font-mono">{log.cost_credits ?? '—'}</dd>
          </div>
          <div className="col-span-2">
            <dt className="text-xs text-muted-foreground mb-1">Query</dt>
            <dd className="p-3 bg-secondary/40 rounded-lg font-mono text-sm break-words">
              {log.query}
            </dd>
          </div>
          {log.error && (
            <div className="col-span-2">
              <dt className="text-xs text-muted-foreground mb-1">Error</dt>
              <dd className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-lg text-sm text-rose-300 font-mono break-words">
                {log.error}
              </dd>
            </div>
          )}
        </dl>
      </div>
    </div>
  );
}
